import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';

const Icon = ({ d, size = 16, strokeWidth = 2, className = '' }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    globe: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M2 12h20', 'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z'],
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0z'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6', 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2', 'M10 11v6', 'M14 11v6'],
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
};

const WORLD_CURRENCIES = [
    { code: 'USD', symbol: '$', name: 'US Dollar', country: 'United States', dialCode: '+1' },
    { code: 'EUR', symbol: '€', name: 'Euro', country: 'Europe', dialCode: '+32' },
    { code: 'GBP', symbol: '£', name: 'British Pound', country: 'United Kingdom', dialCode: '+44' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee', country: 'India', dialCode: '+91' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen', country: 'Japan', dialCode: '+81' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', country: 'Australia', dialCode: '+61' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', country: 'Canada', dialCode: '+1' },
    { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc', country: 'Switzerland', dialCode: '+41' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', country: 'China', dialCode: '+86' },
    { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', country: 'Sweden', dialCode: '+46' },
    { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', country: 'New Zealand', dialCode: '+64' },
    { code: 'MXN', symbol: '$', name: 'Mexican Peso', country: 'Mexico', dialCode: '+52' },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', country: 'Singapore', dialCode: '+65' },
    { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar', country: 'Hong Kong', dialCode: '+852' },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', country: 'Norway', dialCode: '+47' },
    { code: 'KRW', symbol: '₩', name: 'South Korean Won', country: 'South Korea', dialCode: '+82' },
    { code: 'TRY', symbol: '₺', name: 'Turkish Lira', country: 'Turkey', dialCode: '+90' },
    { code: 'RUB', symbol: '₽', name: 'Russian Ruble', country: 'Russia', dialCode: '+7' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', country: 'Brazil', dialCode: '+55' },
    { code: 'ZAR', symbol: 'R', name: 'South African Rand', country: 'South Africa', dialCode: '+27' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', country: 'United Arab Emirates', dialCode: '+971' },
    { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', country: 'Saudi Arabia', dialCode: '+966' },
    { code: 'QAR', symbol: '﷼', name: 'Qatari Riyal', country: 'Qatar', dialCode: '+974' },
    { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar', country: 'Kuwait', dialCode: '+965' },
    { code: 'BHD', symbol: '.د.ب', name: 'Bahraini Dinar', country: 'Bahrain', dialCode: '+973' },
    { code: 'OMR', symbol: 'ر.ع.', name: 'Omani Rial', country: 'Oman', dialCode: '+968' },
    { code: 'PHP', symbol: '₱', name: 'Philippine Peso', country: 'Philippines', dialCode: '+63' },
    { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', country: 'Malaysia', dialCode: '+60' },
    { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah', country: 'Indonesia', dialCode: '+62' },
    { code: 'THB', symbol: '฿', name: 'Thai Baht', country: 'Thailand', dialCode: '+66' },
    { code: 'VND', symbol: '₫', name: 'Vietnamese Dong', country: 'Vietnam', dialCode: '+84' }
].sort((a, b) => a.name.localeCompare(b.name));

const API = '/api/countries';

export default function Countries() {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ name: '', code: '', currency: '', engCurrency: '', exchangeRate: 1, locale: '', taxRate: 0, isActive: true });
    const [showImportScreen, setShowImportScreen] = useState(false);

    const handleExport = () => {
        if (countries.length === 0) { alert('No countries to export.'); return; }
        const exportData = countries.map(c => ({
            'Country Name': c.name || '',
            'Country Code': c.code || '',
            'Currency Symbol': c.currency || '',
            'Currency Code': c.engCurrency || '',
            'Exchange Rate': c.exchangeRate || 1,
            'Locale': c.locale || '',
            'Tax Rate (%)': c.taxRate || 0,
            'Status': c.isActive ? 'Active' : 'Inactive'
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        ws['!cols'] = [
            { wch: 18 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 10 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Countries');
        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Countries_Export_${timestamp}.xlsx`);
    };

    const fetchCountries = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(API);
            const data = await res.json();
            setCountries(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Fetch countries error:", err);
            setCountries([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchCountries();
    }, [fetchCountries]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                name: item.name,
                code: item.code,
                currency: item.currency || '',
                engCurrency: item.engCurrency || '',
                exchangeRate: item.exchangeRate || 1,
                locale: item.locale || '',
                taxRate: item.taxRate,
                isActive: item.isActive
            });
        } else {
            setEditingItem(null);
            setFormData({ name: '', code: '', currency: '', engCurrency: '', exchangeRate: 1, locale: '', taxRate: 0, isActive: true });
        }
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const url = editingItem ? `${API}/${editingItem._id}` : API;
            const method = editingItem ? 'PUT' : 'POST';
            const res = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            if (res.ok) {
                setShowModal(false);
                fetchCountries();
            }
        } catch (err) {
            console.error("Save country error:", err);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this country?")) return;
        try {
            const res = await fetch(`${API}/${id}`, { method: 'DELETE' });
            if (res.ok) fetchCountries();
        } catch (err) {
            console.error("Delete country error:", err);
        }
    };

    const filteredCountries = countries.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (showImportScreen) {
        return (
            <CountryImportWizard
                countries={countries}
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchCountries(); }}
            />
        );
    }

    return (
        <div className="countries-page">
            <div className="superadmin-page-header">
                <div className="header-titles">
                    <h1>Country Management</h1>
                    <p>Configure regional settings, dialing codes, tax rates, and status.</p>
                </div>
                <div className="header-actions">
                    <button
                        onClick={() => setShowImportScreen(true)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 6, border: '1.5px solid #c3dcf5', background: '#eef2ff', color: '#11427D', fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                        <Icon d={icons.upload} size={16} strokeWidth={2.5} />
                        Import
                    </button>
                    <button
                        onClick={handleExport}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 6, border: '1.5px solid #a7f3d0', background: '#ecfdf5', color: '#065f46', fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                        <Icon d={icons.download} size={16} strokeWidth={2.5} />
                        Export
                    </button>
                    <button className="btn-primary" onClick={() => handleOpenModal()} style={{ margin: 0 }}>
                        <Icon d={icons.plus} size={18} /> Add Country
                    </button>
                </div>
            </div>

            <div className="filters-bar">
                <div className="search-input-wrap">
                    <Icon d={icons.search} size={16} className="search-icon-inline" />
                    <input
                        type="text"
                        placeholder="Search countries..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="premium-table-container">
                {loading ? (
                    <div className="empty-state">Loading countries...</div>
                ) : filteredCountries.length === 0 ? (
                    <div className="empty-state">No countries found.</div>
                ) : (
                    <table className="premium-table">
                        <thead>
                            <tr>
                                <th>Country Name</th>
                                <th>Country Code</th>
                                <th>Tax Rate (%)</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCountries.map(c => (
                                <tr key={c._id}>
                                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                                    <td><span className="code-badge">{c.code}</span></td>
                                    <td>{c.taxRate}%</td>
                                    <td>
                                        <span className={`status-pill ${c.isActive ? 'status-active' : 'status-inactive'}`}>
                                            {c.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <button className="icon-btn edit" onClick={() => handleOpenModal(c)}>
                                            <Icon d={icons.edit} size={16} />
                                        </button>
                                        <button className="icon-btn delete" onClick={() => handleDelete(c._id)}>
                                            <Icon d={icons.trash} size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>{editingItem ? 'Edit Country' : 'Add New Country'}</h2>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <Icon d={['M18 6L6 18', 'M6 6l12 12']} size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">Country Name</label>
                                    <input
                                        className="form-input"
                                        placeholder="e.g., India"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Country Dialing Code</label>
                                    <input
                                        className="form-input"
                                        placeholder="e.g., +91"
                                        value={formData.code}
                                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Currency Code</label>
                                        <select
                                            className="form-input"
                                            value={formData.engCurrency}
                                            onChange={(e) => {
                                                const curr = WORLD_CURRENCIES.find(c => c.code === e.target.value);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    engCurrency: e.target.value,
                                                    ...(curr ? { 
                                                        currency: curr.symbol,
                                                        name: prev.name || curr.country,
                                                        code: prev.code || curr.dialCode 
                                                    } : {})
                                                }));
                                            }}
                                            required
                                        >
                                            <option value="">Select Currency</option>
                                            {WORLD_CURRENCIES.map(c => (
                                                <option key={c.code} value={c.code}>
                                                    {c.code} - {c.name}
                                                </option>
                                            ))}
                                            {formData.engCurrency && !WORLD_CURRENCIES.find(c => c.code === formData.engCurrency) && (
                                                <option value={formData.engCurrency}>{formData.engCurrency}</option>
                                            )}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Currency Symbol</label>
                                        <input
                                            className="form-input"
                                            placeholder="e.g., ₹ or $"
                                            value={formData.currency}
                                            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Tax Rate (%)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-input"
                                        placeholder="e.g., 18"
                                        value={formData.taxRate}
                                        onChange={(e) => setFormData({ ...formData, taxRate: parseFloat(e.target.value) })}
                                        required
                                    />
                                </div>
                                <div className="toggle-group">
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={formData.isActive}
                                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                    <span className="toggle-label">Active Operational Status</span>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-save">{editingItem ? 'Update Country' : 'Save Country'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .countries-page {
                    padding: 30px;
                    flex: 1;
                    overflow-y: auto;
                }
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                }
                .page-header h1 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0 0 5px 0;
                }
                .page-header p {
                    color: #64748b;
                    margin: 0;
                }
                .code-badge {
                    background: #f1f5f9;
                    color: #1e3a8a;
                    padding: 4px 10px;
                    border-radius: 6px;
                    font-family: 'JetBrains Mono', monospace;
                    font-weight: 700;
                    font-size: 13px;
                }
                .status-inactive {
                    background: #fee2e2;
                    color: #991b1b;
                }
                .theme-dark .status-inactive {
                    background: rgba(153, 27, 27, 0.2);
                    color: #f87171;
                }
                .theme-dark .code-badge {
                    background: #1e293b;
                    color: #60a5fa;
                }
                .theme-dark .page-header h1 { color: #f8fafc; }
            `}</style>
        </div>
    );
}

function CountryImportWizard({ countries, onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    // Map of existing countries (lowercase name -> true)
    const existingNames = new Set(countries.map(c => (c.name || '').trim().toLowerCase()));

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const processFile = (selectedFile) => {
        setFile(selectedFile);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                // Standardize headers
                const standardized = data.map((row) => {
                    const mapped = {};
                    Object.keys(row).forEach(key => {
                        const cleanKey = key.trim();
                        mapped[cleanKey] = row[key];
                    });
                    return mapped;
                });

                setParsedData(standardized);
            } catch (err) {
                console.error(err);
                alert("Failed to parse Excel file. Please ensure it is a valid .xlsx or .xls file.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Country Name': 'United Kingdom',
                'Country Code': '+44',
                'Currency Symbol': '£',
                'Currency Code': 'GBP',
                'Exchange Rate': 1.25,
                'Locale': 'en-GB',
                'Tax Rate (%)': 20,
                'Status': 'Active'
            },
            {
                'Country Name': 'United States',
                'Country Code': '+1',
                'Currency Symbol': '$',
                'Currency Code': 'USD',
                'Exchange Rate': 1.0,
                'Locale': 'en-US',
                'Tax Rate (%)': 8.5,
                'Status': 'Active'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        ws['!cols'] = [
            { wch: 18 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 10 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, 'Countries_Import_Template.xlsx');
    };

    const handleImport = async () => {
        if (parsedData.length === 0) return;
        setImporting(true);
        try {
            const res = await fetch(`/api/countries/import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ countries: parsedData })
            });
            const data = await res.json();
            if (res.ok) {
                setResult(data);
            } else {
                alert(data.message || "Import failed. Please try again.");
            }
        } catch (err) {
            console.error(err);
            alert("Import failed. Please try again.");
        } finally {
            setImporting(false);
        }
    };

    const matchedCount = parsedData.filter(d => {
        const name = (d.name || d['Country Name'] || d['Name'] || '').trim().toLowerCase();
        return name && existingNames.has(name);
    }).length;

    const newCount = parsedData.filter(d => {
        const name = (d.name || d['Country Name'] || d['Name'] || '').trim().toLowerCase();
        return name && !existingNames.has(name);
    }).length;

    if (result) {
        return (
            <div className="countries-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: '24px' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '12px' }}>Import Completed!</h2>
                    <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '600px', margin: '0 auto 30px' }}>
                        The country data has been processed successfully. Existing countries were updated and new ones were created.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', maxWidth: '700px', margin: '0 auto 30px' }}>
                        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#065f46' }}>{result.updatedCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#047857', marginTop: '4px' }}>Countries Updated</div>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#1e40af' }}>{result.createdCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', marginTop: '4px' }}>New Countries Created</div>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#92400e' }}>{(result.errors?.length) || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#b45309', marginTop: '4px' }}>Skipped / Warnings</div>
                        </div>
                    </div>

                    {result.errors && result.errors.length > 0 && (
                        <div style={{ maxWidth: '750px', margin: '0 auto 30px', textAlign: 'left' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', marginBottom: '10px' }}>Details / Warnings:</h4>
                            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc' }}>
                                {result.errors.map((err, idx) => (
                                    <div key={idx} style={{ fontSize: '13px', color: '#64748b', padding: '6px 0', borderBottom: idx < result.errors.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                                        ⚠️ Row {err.row}: {err.message}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={onDone}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}
                    >
                        Return to Countries
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="countries-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                        onClick={onBack}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                        </svg>
                    </button>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Import & Update Countries</h1>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>Bulk import new countries or update existing regional settings via Excel upload.</p>
                    </div>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '30px' }}>
                    {/* Instructions Left Column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ℹ️ How it works
                            </h3>
                            <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
                                <li>The file must be an Excel sheet (<code>.xlsx</code> or <code>.xls</code>).</li>
                                <li>It matches uploaded rows to <strong>EXISTING</strong> countries in the database by their <strong>Country Name</strong> (case-insensitive) to update their fields.</li>
                                <li style={{ fontWeight: '600', color: '#16539C' }}>Unmatched country names will automatically be created as brand new countries!</li>
                                <li>Currency symbols, dialing codes, tax rates, exchange rates, and locales will be set.</li>
                            </ul>
                        </div>

                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>Supported Fields</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {['Country Name *', 'Country Code *', 'Currency Symbol', 'Currency Code', 'Exchange Rate', 'Locale', 'Tax Rate (%)', 'Status'].map((f, i) => (
                                    <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: '#11427D', background: '#eef2ff', border: '1px solid #c3dcf5', padding: '4px 8px', borderRadius: '6px' }}>
                                        {f}
                                    </span>
                                ))}
                            </div>
                            <p style={{ fontSize: '11.5px', color: '#64748b', marginTop: '10px', margin: '10px 0 0' }}>* <strong>Country Name</strong> and <strong>Country Code</strong> are required to create a new country.</p>
                        </div>

                        <div style={{ background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', padding: '20px', textAlign: 'center' }}>
                            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px' }}>Need a starting point? Download our pre-configured format template.</p>
                            <button
                                onClick={downloadTemplate}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Download Sample Template
                            </button>
                        </div>
                    </div>

                    {/* Upload Box Right Column */}
                    <div
                        onDragEnter={handleDrag}
                        onDragOver={handleDrag}
                        onDragLeave={handleDrag}
                        onDrop={handleDrop}
                        style={{
                            background: 'white',
                            borderRadius: '16px',
                            border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`,
                            backgroundColor: dragActive ? '#f0f7ff' : 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '60px 40px',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                            transition: 'all 0.15s',
                            cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById('file-upload-input').click()}
                    >
                        <input
                            id="file-upload-input"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '16px', background: '#eef2ff', color: '#16539C', marginBottom: '20px' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: '12px', color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
                            Supports .xlsx, .xls formats
                        </span>
                    </div>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '20px', borderBottom: '1px solid #f1f5f9', marginBottom: '20px' }}>
                        <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: 0 }}>Review Countries</h3>
                            <p style={{ fontSize: '12.5px', color: '#64748b', margin: '4px 0 0' }}>
                                Found <strong>{parsedData.length} records</strong>. We will match existing countries by name to update them, and create any new ones.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => { setFile(null); setParsedData([]); }}
                                style={{ padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                            >
                                Reset
                            </button>
                            <button
                                onClick={handleImport}
                                disabled={importing}
                                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)', opacity: importing ? 0.7 : 1 }}
                            >
                                {importing ? 'Processing...' : `Proceed Import (${matchedCount + newCount})`}
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#166534', fontWeight: '600' }}>To Update (Match)</span>
                            <span style={{ fontSize: '18px', color: '#15803d', fontWeight: '800' }}>{matchedCount}</span>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#1e40af', fontWeight: '600' }}>To Create (New)</span>
                            <span style={{ fontSize: '18px', color: '#1d4ed8', fontWeight: '800' }}>{newCount}</span>
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto', maxHeight: '350px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Country Name</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Dialing Code</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Currency Symbol</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Currency Code</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Exchange Rate</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Locale</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Tax Rate (%)</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Import Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedData.map((row, idx) => {
                                    const name = (row.name || row['Country Name'] || row['Name'] || '').trim();
                                    const code = row.code || row['Country Code'] || '';
                                    const symbol = row.currency || row['Currency Symbol'] || '';
                                    const currCode = row.engCurrency || row['Currency Code'] || '';
                                    const rate = row.exchangeRate || row['Exchange Rate'] || 1.0;
                                    const loc = row.locale || row['Locale'] || '';
                                    const tax = row.taxRate || row['Tax Rate (%)'] || 0;
                                    
                                    const isMatch = name && existingNames.has(name.toLowerCase());
                                    const actionText = isMatch ? 'Update Existing' : 'Create New';
                                    const actionBg = isMatch ? '#f0fdf4' : '#eff6ff';
                                    const actionColor = isMatch ? '#166534' : '#1e40af';

                                    return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '12px 16px', fontWeight: '600' }}>{name || <span style={{ color: '#ef4444' }}>Missing Name</span>}</td>
                                            <td style={{ padding: '12px 16px' }}>{code}</td>
                                            <td style={{ padding: '12px 16px' }}>{symbol}</td>
                                            <td style={{ padding: '12px 16px' }}>{currCode}</td>
                                            <td style={{ padding: '12px 16px' }}>{rate}</td>
                                            <td style={{ padding: '12px 16px' }}>{loc}</td>
                                            <td style={{ padding: '12px 16px' }}>{tax}%</td>
                                            <td style={{ padding: '12px 16px' }}>
                                                <span style={{ fontSize: '11px', fontWeight: '700', padding: '4px 8px', borderRadius: '4px', backgroundColor: actionBg, color: actionColor }}>
                                                    {actionText}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
